import React, { useState } from "react";
import { 
  Package, 
  Sparkles, 
  ShieldCheck, 
  Building2, 
  PhoneCall, 
  CheckCircle, 
  Send,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import { PAKISTAN_CITIES } from "@/constants/pakistanCities";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import apiClient from "@/utils/api";

export default function Wholesale() {
  const { settings } = useSiteSettings();
  const [businessName, setBusinessName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [city, setCity] = useState("Lahore");
  const [volume, setVolume] = useState("50-100 Sets");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const categories = [
    "Diamond Zari Sitara Lace",
    "Medium Size Diamond Sitara",
    "Zero Size Diamond Sitara",
    "Organza Lace",
    "Net Milky Lace",
    "Bridal Lace",
    "Sequence Galla",
    "Fashion Accessories",
    "Milky White Black Lace",
    "Milky Color Lace"
  ];
  const [selectedCats, setSelectedCats] = useState(["Diamond Zari Sitara Lace", "Bridal Lace"]);

  const toggleCategory = (cat) => {
    if (selectedCats.includes(cat)) {
      setSelectedCats(selectedCats.filter((c) => c !== cat));
    } else {
      setSelectedCats([...selectedCats, cat]);
    }
  };

  const handleWholesaleSubmit = async (e) => {
    e.preventDefault();
    if (!businessName || !contactPerson || !mobileNumber || !message) {
      toast.error("Please fill in business name, contact person, phone, and requirements.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        business_name: businessName.trim(),
        contact_person: contactPerson.trim(),
        mobile_number: mobileNumber.trim(),
        whatsapp_number: (whatsappNumber || mobileNumber).trim(),
        city: city,
        product_interests: selectedCats,
        estimated_monthly_volume: volume,
        message: message.trim()
      };

      const { data } = await apiClient.post("/wholesale-inquiries", payload);
      if (data && data.success) {
        setSubmitted(true);
        toast.success("Wholesale inquiry submitted successfully!");
      } else {
        toast.error("Could not submit inquiry.");
      }
    } catch (err) {
      console.error("Wholesale submit error", err);
      toast.error("Submission failed. You can directly chat on WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
      
      {/* Hero Header */}
      <div className="bg-[#1C1917] text-white rounded-3xl border-2 border-[#D4AF37] p-8 sm:p-14 relative overflow-hidden shadow-2xl">
        <div className="relative z-10 max-w-2xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#D4AF37]/20 text-[#E6C687] text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            Boutique & Retail Supply Division
          </div>
          <h1 
            data-testid="wholesale-page-title"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight"
          >
            Direct Wholesale & Bulk Lace Supply in Pakistan
          </h1>
          <p className="text-stone-300 text-xs sm:text-sm leading-relaxed">
            Partner with Anwer Fancy Lace for direct China import pricing on complete sets, rolls, kora-dabka bridal trims, and zero-size sitara laces. We supply fashion houses, embroidery units, and retail shops across all major Pakistani markets.
          </p>
        </div>
      </div>

      {/* 3 Pillars of Wholesale */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-2">
          <h3 className="font-heading font-bold text-base text-stone-900">Direct China Factory Pricing</h3>
          <p className="text-xs text-stone-600 leading-relaxed">
            No middleman markups. Receive factory rates on carton quantities and complete set bundles.
          </p>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-2">
          <h3 className="font-heading font-bold text-base text-stone-900">Guaranteed Complete Set Yardage</h3>
          <p className="text-xs text-stone-600 leading-relaxed">
            Pre-cut matching Complete Set bundles with defined Colors, Taan and Gaz — ready for instant retail packaging or design manufacturing.
          </p>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-2">
          <h3 className="font-heading font-bold text-base text-stone-900">Fast Inter-City Cargo Dispatch</h3>
          <p className="text-xs text-stone-600 leading-relaxed">
            Same-day or next-day booking via Daewoo Cargo, Faisal Movers, TCS, or Bilal Cargo to your city.
          </p>
        </div>
      </div>

      {/* Inquiry Form & Wholesale Desk */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* Form (7 cols) */}
        <div className="lg:col-span-7">
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-[#E5E0D8] shadow-luxury space-y-6">
            <div>
              <h2 className="font-heading text-xl font-bold text-stone-900">
                Submit Wholesale Order Inquiry
              </h2>
              <p className="text-xs text-stone-500 mt-1">
                Fill out your business requirements. Our wholesale account manager will contact you with rate sheets and catalog samples.
              </p>
            </div>

            {submitted ? (
              <div 
                data-testid="wholesale-success-box"
                className="bg-emerald-50 border border-emerald-300 rounded-2xl p-8 text-center space-y-4"
              >
                <CheckCircle className="w-12 h-12 text-emerald-700 mx-auto" />
                <h3 className="font-heading font-bold text-lg text-emerald-950">Inquiry Received Successfully</h3>
                <p className="text-xs text-emerald-800 max-w-md mx-auto">
                  Thank you! Our wholesale team has received your request and will connect with you on WhatsApp within market hours.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="px-5 py-2 bg-[#1C1917] text-white text-xs font-bold rounded-lg"
                >
                  Submit Another Inquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleWholesaleSubmit} className="space-y-4 text-xs">
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="font-semibold text-stone-800">
                      Business / Boutique / Store Name <span className="text-rose-600">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      data-testid="wholesale-business-name-input"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="e.g. Sana Couture / Lahore Lace Traders"
                      className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-semibold text-stone-800">
                      Contact Person Name <span className="text-rose-600">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      data-testid="wholesale-contact-name-input"
                      value={contactPerson}
                      onChange={(e) => setContactPerson(e.target.value)}
                      placeholder="e.g. Tariq Mehmood"
                      className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="font-semibold text-stone-800">
                      Mobile Number <span className="text-rose-600">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      data-testid="wholesale-mobile-input"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value)}
                      placeholder="0300 1234567"
                      className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-semibold text-stone-800">
                      WhatsApp Number
                    </label>
                    <input
                      type="tel"
                      data-testid="wholesale-whatsapp-input"
                      value={whatsappNumber}
                      onChange={(e) => setWhatsappNumber(e.target.value)}
                      placeholder="0300 1234567"
                      className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-semibold text-stone-800">
                      City in Pakistan <span className="text-rose-600">*</span>
                    </label>
                    <select
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    >
                      {PAKISTAN_CITIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Categories of Interest */}
                <div className="space-y-1.5">
                  <label className="font-semibold text-stone-800 block">
                    Product Categories of Interest:
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {categories.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleCategory(c)}
                        className={`px-2.5 py-1 rounded-md border text-[11px] font-medium transition ${
                          selectedCats.includes(c)
                            ? "bg-[#1C1917] text-white border-[#1C1917]"
                            : "bg-[#FAF8F5] text-stone-700 border-stone-300 hover:border-stone-400"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Estimated Volume */}
                <div className="space-y-1">
                  <label className="font-semibold text-stone-800">
                    Estimated Monthly Requirement:
                  </label>
                  <select
                    value={volume}
                    onChange={(e) => setVolume(e.target.value)}
                    className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                  >
                    <option value="20-50 Sets">20 - 50 Complete Sets / Small Boutique</option>
                    <option value="50-100 Sets">50 - 100 Complete Sets / Retail Shop</option>
                    <option value="100-500 Sets">100 - 500 Complete Sets / Wholesale Distributor</option>
                    <option value="500+ Sets">500+ Sets / Factory Rolls (Bulk Importer)</option>
                  </select>
                </div>

                {/* Message */}
                <div className="space-y-1">
                  <label className="font-semibold text-stone-800">
                    Specific Lace Requirements / Design Numbers <span className="text-rose-600">*</span>
                  </label>
                  <textarea
                    required
                    rows={3}
                    data-testid="wholesale-message-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Provide details about what laces, colors, sitara sizes, or full rolls you need..."
                    className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="wholesale-submit-btn"
                  className="w-full py-3 px-4 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  <span>{submitting ? "Submitting Inquiry..." : "Submit Wholesale Inquiry"}</span>
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Right Wholesale Desk (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-[#FAF8F5] p-6 rounded-3xl border border-[#D4AF37]/50 shadow-sm space-y-5">
            <div className="space-y-1">
              <span className="text-xs font-bold uppercase tracking-wider text-[#996515]">Direct Desk</span>
              <h3 className="font-heading font-bold text-lg text-stone-900">
                Instant WhatsApp Quotation
              </h3>
              <p className="text-xs text-stone-600">
                Prefer immediate wholesale catalogs or videos of current lot stocks? Chat with our wholesale manager.
              </p>
            </div>

            <a
              href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=Assalam%20o%20Alaikum!%20I%20am%20a%20retailer/boutique%20owner%20and%20need%20wholesale%20rates%20for%20Anwer%20Fancy%20Lace.`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wholesale-whatsapp-direct-link"
              className="w-full py-3.5 px-4 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs rounded-xl flex items-center justify-center gap-2 shadow-md transition"
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp Wholesale Desk ({settings.whatsapp_number})</span>
            </a>

            <div className="p-4 bg-white rounded-xl border border-stone-200 text-xs space-y-2">
              <h4 className="font-bold text-stone-900">Wholesale Terms & Conditions:</h4>
              <ul className="list-disc pl-4 space-y-1 text-stone-600">
                <li>Minimum Order Quantity (MOQ): 5 sets per design or 1 full factory carton.</li>
                <li>Custom dyed colors possible for bulk orders of 100+ meters.</li>
                <li>Dispatch within 24 hours of payment verification.</li>
              </ul>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
